'use client';

import { FC, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Smartphone } from 'lucide-react';
import { Spinner } from 'react-bootstrap';
import OtpInput from 'react-otp-input';
import PrimaryButton from './PrimaryButton';
import AuthFooter from './AuthFooter';
import shellStyles from './AuthShell.module.scss';
import styles from './VerifyScreen.module.scss';
interface VerifyScreenProps {
  phoneNumber: string;
  isLoading?: boolean;
  isInvalid?: boolean;
  onVerify: (otp: string) => void | Promise<void>;
  onResend: () => void | Promise<void>;
  onSkip?: () => void;
  termLink?: string;
  privacyPolicyLink?: string;
  otpLength?: number;
}
const VerifyScreen: FC<VerifyScreenProps> = ({
  phoneNumber,
  isLoading = false,
  isInvalid = false,
  onVerify,
  onResend,
  onSkip,
  termLink,
  privacyPolicyLink,
  otpLength = 4
}) => {
  const t = useTranslations();
  const [otp, setOtp] = useState('');
  const [timeLeft, setTimeLeft] = useState(60);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    startCountdown();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);
  const startCountdown = () => {
    setTimeLeft(60);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  const handleResend = async () => {
    await onResend();
    setOtp('');
    startCountdown();
  };
  const handleVerify = async () => {
    if (otp.length === otpLength) {
      await onVerify(otp);
    }
  };
  const ready = otp.length === otpLength;
  const minutes = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const seconds = String(timeLeft % 60).padStart(2, '0');
  return <>
            <div className={shellStyles.iconBubble}>
                <span className={shellStyles.iconBubbleInner}>
                    <Smartphone size={32} strokeWidth={1.75} data-sentry-element="Smartphone" data-sentry-source-file="VerifyScreen.tsx" />
                </span>
            </div>

            <h2 className={shellStyles.heading}>
                {t('auth.signUp.verify.title')}
            </h2>
            <p className={`${shellStyles.subtitle} ${styles.subtitle}`}>
                {t('auth.signUp.verify.subtitle')}{' '}
                <span className={styles.subtitleStrong}>{phoneNumber}</span>.
                <br />
                {t('auth.signUp.verify.enterCode')}
            </p>

            <div className={styles.otpRow}>
                <OtpInput value={otp} onChange={(value: string) => setOtp(value.replace(/\D/g, ''))} inputType="tel" numInputs={otpLength} shouldAutoFocus
      // The library renders its own flex container around the
      // inputs; pass spacing here so it lands on the real
      // parent of the input elements.
      containerStyle={{
        display: 'flex',
        justifyContent: 'center',
        gap: '8px'
      }} renderInput={(props, idx) => {
        // react-otp-input injects an inline `style` (default
        // width: 1em) which beats our CSS-module width rule.
        // Strip it so .otpInput controls sizing.
        const {
          style: _drop,
          ...rest
        } = props;
        return <input {...rest} inputMode="numeric" className={`${styles.otpInput} ${otp[idx] ? styles.filled : ''} ${isInvalid ? styles.invalid : ''}`} />;
      }} data-sentry-element="OtpInput" data-sentry-source-file="VerifyScreen.tsx" />
            </div>

            <PrimaryButton type="button" onClick={handleVerify} disabled={!ready || isLoading} data-sentry-element="PrimaryButton" data-sentry-source-file="VerifyScreen.tsx">
                {isLoading ? <Spinner animation="border" size="sm" /> : t('auth.signUp.verify.verify')}
            </PrimaryButton>

            {isInvalid && <p className={styles.otpError}>
                    {t('auth.signUp.verify.error')}
                </p>}

            <p className={styles.resend}>
                {timeLeft > 0 ? <>
                        {t('auth.signUp.verify.resendIn')}{' '}
                        <span className={styles.resendStrong}>
                            {minutes}:{seconds}
                        </span>
                    </> : <button type="button" className={styles.resendBtn} onClick={handleResend}>
                        {t('auth.signUp.verify.resend')}
                    </button>}
            </p>

            {onSkip && <button type="button" className={styles.skip} onClick={onSkip}>
                    {t('auth.signUp.verify.later')}
                </button>}

            <AuthFooter termLink={termLink} privacyPolicyLink={privacyPolicyLink} data-sentry-element="AuthFooter" data-sentry-source-file="VerifyScreen.tsx" />
        </>;
};
export default VerifyScreen;